import { ApolloQueryResult } from '@apollo/client';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import {
  DateRangeOutput,
  GetPaginatedAppointmentsQuery,
  QueryMode,
  SortOrder,
  SubscribeToClinicUserChannelsDocument,
  useGetPaginatedAppointmentsQuery,
} from 'shared/types/graphql';

interface UseGetPaginatedAppointmentsProps {
  clinicId?: string;
  numberOfAppointmentsPerPage?: number;
  roomIds?: string[];
  employeeIds?: string[];
  appointmentTypeIds?: string[];
  dateRange?: DateRangeOutput | null;
  isNewGroup?: boolean;
  petName?: string;
  channelStatusIds?: string[];
  hasNoStatus?: boolean;
  shouldSkip?: boolean;
  shouldFetchCountOnly?: boolean;
}

interface UseGetPaginatedAppointmentsReturn {
  appointmentsPage: number;
  setAppointmentsPage: Dispatch<SetStateAction<number>>;
  appointments?: GetPaginatedAppointmentsQuery['appointments'];
  totalAppointments?: number;
  refetchAppointments: () => Promise<ApolloQueryResult<GetPaginatedAppointmentsQuery>>;
  isLoadingAppointments: boolean;
  hasNextPage: boolean;
}

export const DEFAULT_ITEMS_PER_PAGE = 10;

export function useGetPaginatedAppointments({
  clinicId,
  numberOfAppointmentsPerPage = DEFAULT_ITEMS_PER_PAGE,
  roomIds,
  appointmentTypeIds,
  dateRange,
  employeeIds,
  isNewGroup,
  petName,
  channelStatusIds,
  hasNoStatus = false,
  shouldSkip,
  shouldFetchCountOnly = true,
}: UseGetPaginatedAppointmentsProps): UseGetPaginatedAppointmentsReturn {
  const [appointmentsPage, setAppointmentsPage] = useState(1);

  const queryVarsWhere = useMemo(
    () => ({
      clinicId: { equals: clinicId },
      isDeleted: { equals: false },
      roomId: roomIds?.length ? { in: roomIds } : undefined,
      appointmentTypeId: appointmentTypeIds?.length ? { in: appointmentTypeIds } : undefined,
      clinicEmployees: employeeIds?.length ? { some: { id: { in: employeeIds } } } : undefined,
      startAt: { gte: dateRange?.gte, lte: dateRange?.lte },
      clinicPet: petName ? { name: { contains: petName, mode: QueryMode.Insensitive } } : undefined,
      ...(hasNoStatus && { channelId: { equals: null } }),
      channel: channelStatusIds?.length ? { channelStatusId: { in: channelStatusIds } } : undefined,
    }),
    [appointmentTypeIds, channelStatusIds, clinicId, dateRange, employeeIds, hasNoStatus, petName, roomIds],
  );

  const {
    data: appointmentsData,
    refetch: refetchAppointments,
    loading: isLoadingAppointments,
    subscribeToMore: subscribeToChannels,
  } = useGetPaginatedAppointmentsQuery({
    variables: {
      where: queryVarsWhere,
      take: numberOfAppointmentsPerPage,
      skip: (appointmentsPage - 1) * numberOfAppointmentsPerPage,
      orderBy: { startAt: SortOrder.Asc },
      countOnly: shouldFetchCountOnly,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !clinicId || isNewGroup || shouldSkip,
  });

  const totalAppointments = useMemo(() => appointmentsData?.count, [appointmentsData?.count]);

  const hasNextPage = useMemo(() => {
    return (totalAppointments || 0) > appointmentsPage * numberOfAppointmentsPerPage;
  }, [totalAppointments, appointmentsPage, numberOfAppointmentsPerPage]);

  subscribeToChannels({
    document: SubscribeToClinicUserChannelsDocument,
    variables: { clinicId },
    updateQuery: (prev) => {
      if (prev.count > 0) {
        if (channelStatusIds?.length) {
          refetchAppointments({
            where: {
              ...queryVarsWhere,
              channel: { channelStatusId: { in: channelStatusIds as string[] } },
            },
          });
        }

        return prev;
      } else {
        return { appointments: [], count: 0 };
      }
    },
  });

  useEffect(() => {
    setAppointmentsPage(1);
  }, [clinicId, petName]);

  return {
    appointmentsPage,
    setAppointmentsPage,
    appointments: appointmentsData?.appointments,
    totalAppointments,
    refetchAppointments,
    isLoadingAppointments,
    hasNextPage,
  };
}
